import {
  faCalendarAlt,
  faChartBar,
  faCheckCircle,
  faExclamationTriangle,
  faFileUpload,
  faHistory,
  faInfoCircle,
  faSpinner,
  faUpload
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Grid, Group, Loader, Stack, Table, Text, TextInput } from "@mantine/core";
import ABAApi from "../../../data/api/abaApi";

interface IBSBStats {
  total: number;
  active: number;
  closed: number;
  deleted: number;
  lastUpdated?: string;
}

interface IBSBUploadResult {
  success: boolean;
  data?: {
    message: string;
    stats: any;
    updateId: string;
    errors?: Array<{
      bsb: string;
      error: string;
    }>;
  };
  error?: string;
}

interface IBSBUpdateLog {
  _id: string;
  updateId: string;
  startedAt: string;
  completedAt?: string;
  status: 'processing' | 'completed' | 'failed';
  source: {
    type: 'csv-upload';
    filename: string;
    fileHash?: string;
  };
  stats: {
    totalRecords: number;
    newRecords: number;
    updatedRecords: number;
    deletedRecords: number;
    errors: number;
  };
}

interface BSBUploadTabProps {

}

const BSBUploadTab: React.FunctionComponent<BSBUploadTabProps> = ({

}) => {
  const [stats, setStats] = useState<IBSBStats | null>(null);
  const [updateLogs, setUpdateLogs] = useState<IBSBUpdateLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<IBSBUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);



  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsResult, logsResult] = await Promise.all([
        ABAApi.getBSBStats(),
        ABAApi.getBSBUpdateLogs(10)
      ]);

      if (statsResult.success && statsResult.data) {
        setStats(statsResult.data.stats);
      } else {
        setError(statsResult.error || 'Failed to load BSB statistics');
      }

      if (logsResult.success && logsResult.data) {
        setUpdateLogs(logsResult.data);
      }
    } catch (err) {
      setError('Failed to load BSB data');
      console.error('Failed to load BSB data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setError(null);
        setUploadResult(null);
      } else {
        setError('Please select a CSV file');
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a CSV file first');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const result = await ABAApi.uploadBSBCSV(selectedFile);
      setUploadResult(result);

      if (result.success) {
        // Refresh data after successful upload
        await loadData();
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('csvFile') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed unexpectedly');
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge color="green"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Completed</Badge>;
      case 'processing':
        return <Badge color="yellow"><FontAwesomeIcon icon={faSpinner} spin className="me-1" />Processing</Badge>;
      case 'failed':
        return <Badge color="red"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />Failed</Badge>;
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  return (
    <div>
      {/* Statistics Card */}
      <Card className="mb-4">
        <Card.Section withBorder p="md" className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faChartBar} className="me-2" />
            BSB Directory Statistics
          </h5>
          <Button color="blue" variant="outline" onClick={loadData} disabled={loading}>
            <FontAwesomeIcon icon={faSpinner} spin={loading} className="me-1" />
            Refresh
          </Button>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {loading ? (
            <div className="text-center py-4">
              <Loader color="blue" />
              <p className="mt-2">Loading BSB statistics...</p>
            </div>
          ) : stats ? (
            <Grid>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <div className="text-center">
                  <h3 className="text-primary">{stats.total.toLocaleString()}</h3>
                  <small className="text-muted">Total Records</small>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <div className="text-center">
                  <h3 className="text-success">{stats.active.toLocaleString()}</h3>
                  <small className="text-muted">Active BSBs</small>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <div className="text-center">
                  <h3 className="text-warning">{stats.closed.toLocaleString()}</h3>
                  <small className="text-muted">Closed BSBs</small>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <div className="text-center">
                  <h3 className="text-danger">{stats.deleted.toLocaleString()}</h3>
                  <small className="text-muted">Deleted BSBs</small>
                </div>
              </Grid.Col>
              {stats.lastUpdated && (
                <Grid.Col span={{ base: 12, md: 6 }} className="mt-3">
                  <small className="text-muted">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                    Last updated: {formatDate(stats.lastUpdated)}
                  </small>
                </Grid.Col>
              )}
            </Grid>
          ) : (
            <Alert color="red">{error}</Alert>
          )}
        </div>
      </Card>

      {/* Upload Card */}
      <Card className="mb-4">
        <Card.Section withBorder p="md">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faFileUpload} className="me-2" />
            Upload BSB Directory CSV
          </h5>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          <Alert color="blue">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Upload a BSB directory CSV file from the Australian Payments Network.
            The system will automatically detect changes and only update records that have been modified.
          </Alert>

          <Stack gap="md">
            <Stack gap="sm">
            <label htmlFor="csvFile" className="fw-500">Select CSV File</label>
              <TextInput
                type="file"
                id="csvFile"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              {selectedFile && (
                <div className="mt-2">
                  <small className="text-muted">
                    Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </small>
                </div>
              )}
            </Stack>

            <Button
              color="blue"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              <FontAwesomeIcon
                icon={uploading ? faSpinner : faUpload}
                spin={uploading}
                className="me-2"
              />
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </Stack>

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-4">
              {uploadResult.success ? (
                <Alert color="green">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  <strong>Upload completed successfully!</strong>
                  {uploadResult.data && (
                    <div className="mt-3">
                      <h6>Update Statistics:</h6>
                      <Grid>
                        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                          <div className="text-center">
                            <div className="h5 mb-0">{uploadResult.data.stats.totalRecords.toLocaleString()}</div>
                            <small>Total Processed</small>
                          </div>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                          <div className="text-center">
                            <div className="h5 mb-0 text-success">{uploadResult.data.stats.newRecords.toLocaleString()}</div>
                            <small>New Records</small>
                          </div>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                          <div className="text-center">
                            <div className="h5 mb-0 text-info">{uploadResult.data.stats.updatedRecords.toLocaleString()}</div>
                            <small>Updated Records</small>
                          </div>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                          <div className="text-center">
                            <div className="h5 mb-0 text-warning">{uploadResult.data.stats.deletedRecords.toLocaleString()}</div>
                            <small>Deleted Records</small>
                          </div>
                        </Grid.Col>
                      </Grid>
                      {uploadResult.data.stats.errors > 0 && (
                        <div className="mt-2">
                          <Badge color="yellow">
                            {uploadResult.data.stats.errors} errors occurred during processing
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}
                </Alert>
              ) : (
                <Alert color="red">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Upload failed:</strong> {uploadResult.error}
                </Alert>
              )}
            </div>
          )}

          {error && !uploadResult && (
            <Alert color="red" className="mt-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {error}
            </Alert>
          )}
        </div>
      </Card>

      {/* Recent Update Logs */}
      <Card>
        <Card.Section withBorder p="md">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Recent Update Logs
          </h5>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {updateLogs.length > 0 ? (
            <Table striped>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Records</th>
                  <th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {updateLogs.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <div>{formatDate(log.startedAt)}</div>
                      {log.completedAt && (
                        <small className="text-muted">
                          Completed: {formatDate(log.completedAt)}
                        </small>
                      )}
                    </td>
                    <td>{getStatusBadge(log.status)}</td>
                    <td>
                      <div>{log.source.type}</div>
                      <small className="text-muted">{log.source.filename}</small>
                    </td>
                    <td>{log.stats.totalRecords.toLocaleString()}</td>
                    <td>
                      <div className="small">
                        <Badge color="green" className="me-1">+{log.stats.newRecords}</Badge>
                        <Badge color="blue" className="me-1">~{log.stats.updatedRecords}</Badge>
                        <Badge color="yellow" className="me-1">-{log.stats.deletedRecords}</Badge>
                        {log.stats.errors > 0 && (
                          <Badge color="red">!{log.stats.errors}</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              No update logs available
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};


export default BSBUploadTab;
